import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';

type LoadingContextValue = {
  loading: boolean;
  loadingText: string;
  beginLoading: (text?: string) => () => void;
  beginLoadingKey: (key: keyof typeof LOADING_TEXTS) => () => void;
  setLoadingText: (text: string) => void;
};

const LoadingContext = createContext<LoadingContextValue | null>(null);

const LOADING_TEXTS = {
  frontendConfigLoad: 'Frontend-Konfiguration wird geladen …',
  adminConfigLoad: 'Admin-Konfiguration wird geladen …',
  adminConfigSave: 'Konfiguration wird gespeichert …',
  assistantsLoad: 'Assistenten werden geladen …',
  promptsLoad: 'Prompts werden geladen …',
  profileFilesLoad: 'Profile werden geladen …',
  profileUpload: 'Profil wird hochgeladen …',
  profileSuggestion: 'Profilvorschlag wird geladen …',
  assistantSave: 'Assistent wird gespeichert …',
  assistantDelete: 'Assistent wird gelöscht …',
} as const;

export const useLoadingContext = () => {
  const value = useContext(LoadingContext);
  if (!value) throw new Error('LoadingContext missing');
  return value;
};

export const LoadingProvider = ({ children }: { children: React.ReactNode }) => {
  const [count, setCount] = useState(0);
  const [loadingText, setLoadingText] = useState('');

  const beginLoading = useCallback((text?: string) => {
    if (text) setLoadingText(text);
    setCount((prev) => prev + 1);
    let closed = false;
    return () => {
      if (closed) return;
      closed = true;
      setCount((prev) => Math.max(0, prev - 1));
    };
  }, []);

  const beginLoadingKey = useCallback((key: keyof typeof LOADING_TEXTS) => (
    beginLoading(LOADING_TEXTS[key])
  ), [beginLoading]);

  const value = useMemo(() => ({
    loading: count > 0,
    loadingText,
    beginLoading,
    beginLoadingKey,
    setLoadingText,
  }), [count, loadingText, beginLoading, beginLoadingKey]);

  // Clear stale text when all loading operations complete.
  useEffect(() => {
    if (count === 0 && loadingText) {
      setLoadingText('');
    }
  }, [count, loadingText]);

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};
